// @noflow
import { rgb, getLuminance } from 'polished';
import { flowRight, map, spread } from 'lodash/fp';

const temperatureColours = {
    5: { r: 80, g: 181, b: 221 },
    6: { r: 78, g: 178, b: 206 },
    7: { r: 76, g: 176, b: 190 },
    8: { r: 73, g: 173, b: 175 },
    9: { r: 72, g: 171, b: 159 },
    10: { r: 70, g: 168, b: 142 },
    11: { r: 68, g: 166, b: 125 },
    12: { r: 66, g: 164, b: 108 },
    13: { r: 102, g: 173, b: 94 },
    14: { r: 135, g: 190, b: 64 },
    15: { r: 179, g: 204, b: 26 },
    16: { r: 214, g: 213, b: 28 },
    17: { r: 249, g: 202, b: 3 },
    18: { r: 246, g: 181, b: 3 },
    19: { r: 244, g: 150, b: 26 },
    20: { r: 236, g: 110, b: 5 },
    21: { r: 234, g: 90, b: 36 },
    22: { r: 228, g: 87, b: 43 },
    23: { r: 225, g: 74, b: 41 },
    24: { r: 224, g: 65, b: 39 },
    25: { r: 217, g: 55, b: 43 },
    26: { r: 214, g: 49, b: 41 },
    27: { r: 209, g: 43, b: 43 },
    28: { r: 205, g: 40, b: 47 },
    29: { r: 200, g: 36, b: 50 },
    30: { r: 195, g: 35, b: 52 },
    31: { r: 190, g: 33, b: 56 },
    32: { r: 185, g: 32, b: 59 }
};

const hexTable = {
    0: 0,
    1: 1,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 'A',
    11: 'B',
    12: 'C',
    13: 'D',
    14: 'E',
    15: 'F'
};

const getHexParts = part => {
    const partY = part % 16;
    const partX = (part - partY) / 16;

    return [hexTable[partX], hexTable[partY]];
};

export function getColour(temperature) {
    temperature = temperature < 5 ? 5 : temperature;
    temperature = temperature > 32 ? 32 : temperature;
    const t = temperatureColours[Math.floor(temperature)];
    return `rgb(${t.r}, ${t.g}, ${t.b})`;
}

export function getColourRGB(temperature) {
    temperature = temperature < 5 ? 5 : temperature;
    temperature = temperature > 32 ? 32 : temperature;
    return temperatureColours[Math.floor(temperature)];
}

export function getRangeColours(maxTemp, minTemp, interval = 1) {
    const rangeColors = [];
    for (let i = maxTemp; i >= minTemp; i -= interval) {
        rangeColors.push(getColourRGB(parseInt(i, 10)));
    }
    return rangeColors;
}

export function getRangeColourStrings(maxTemp, minTemp, interval = 1) {
    return getRangeColours(maxTemp, minTemp, interval).map(color => {
        return `rgb(${color.r}, ${color.g}, ${color.b})`;
    });
}

export function convertRGBToHex(rgbString) {
    const numbers = rgbString
        .split(' ')
        .join('')
        .substr(4)
        .slice(0, -1)
        .split(',');
    const r = getHexParts(numbers[0]).join('');
    const g = getHexParts(numbers[1]).join('');
    const b = getHexParts(numbers[2]).join('');

    return '#' + r + g + b;
}

export function convertTemperatureToRGB(kelvin) {
    // !!!WARNING!!!
    // This is not the acurate way to get the rgb from a temperature but just an approximation
    // to get a colour which is close to the ones shown in the image used in the tuneable light picker
    // (this image itself is not accurate). Only use for display purpose.
    const green = 246;
    const blue = Math.round(Math.sin(Math.PI / 2 * (kelvin - 4500) / 1500) * 35 + 219);
    const red = Math.round(-Math.sin(Math.PI / 2 * (kelvin - 4500) / 1500) * 25 + 230);

    return `rgb(${red}, ${green}, ${blue})`;
}

export function hsvToRgb(hsv) {
    const h = hsv.h / 60;
    const s = hsv.s / 100;
    let v = hsv.v / 100;
    const hi = Math.floor(h) % 6;

    const f = h - Math.floor(h);
    const p = 255 * v * (1 - s);
    const q = 255 * v * (1 - s * f);
    const t = 255 * v * (1 - s * (1 - f));
    let rgb;
    v *= 255;

    switch (hi) {
        case 0:
            rgb = [v, t, p];
            break;
        case 1:
            rgb = [q, v, p];
            break;
        case 2:
            rgb = [p, v, t];
            break;
        case 3:
            rgb = [p, q, v];
            break;
        case 4:
            rgb = [t, p, v];
            break;
        case 5:
            rgb = [v, p, q];
            break;
        default:
            rgb = [0, 0, 0];
            break;
    }

    const [red, green, blue] = rgb.map(Math.round);
    return { red, green, blue };
}

export const hsvToRgbString = flowRight(rgb, hsvToRgb);

export function rgbToHsv(rgb) {
    const [r, g, b] = rgb;
    const min = Math.min(r, g, b);
    const max = Math.max(r, g, b);
    const delta = max - min;
    let h;
    let s;

    if (max === 0) {
        s = 0;
    } else {
        s = delta / max * 1000 / 10;
    }

    if (max === min) {
        h = 0;
    } else if (r === max) {
        h = (g - b) / delta;
    } else if (g === max) {
        h = 2 + (b - r) / delta;
    } else if (b === max) {
        h = 4 + (r - g) / delta;
    }

    h = Math.min(h * 60, 360);

    if (h < 0) {
        h += 360;
    }

    let v = max / 255 * 1000 / 10;

    [h, s, v] = [h, s, v].map(Math.round);
    return { h, s, v };
}

export function rgbToRgbString(rgb) {
    return rgb.length >= 3 ? `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})` : 'rgb(0, 0, 0)';
}

const contrastRatio = (lum1, lum2) => (lum1 + 0.05) / (lum2 + 0.05);
const higherLuminance = (lum1, lum2) => (lum1 > lum2 ? [lum1, lum2] : [lum2, lum1]);
const luminance = (...colors) => map(getLuminance, [...colors]);

export const contrastRatioFromColours = flowRight(spread(contrastRatio), spread(higherLuminance), luminance);



// WEBPACK FOOTER //
// ./src/core/utils/colour.js